import { Node as FlowNode, Edge } from 'reactflow';
import {
  Copy,
  Trash2,
  Clipboard,
  Boxes,
  Ungroup,
  Lock,
  Unlock,
  Palette,
  Plus,
  Maximize,
  Grid3X3,
  FolderOpen,
  Workflow,
  MousePointerSquareDashed,
  Image as ImageIcon,
  Play,
  Spline,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignCenterHorizontal,
  AlignEndHorizontal,
  AlignHorizontalDistributeCenter,
  AlignVerticalDistributeCenter,
  Pencil,
  Layers,
  BringToFront,
  SendToBack,
  ArrowUp,
  ArrowDown,
  Crosshair,
} from 'lucide-react';
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuLabel,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
  ContextMenuCheckboxItem,
} from '../../components/ui/beui';
import type { AlignType, DistributeAxis } from './alignment';

export type CtxTarget =
  | { type: 'pane' }
  | { type: 'node'; node: FlowNode }
  | { type: 'edge'; edge: Edge }
  | { type: 'selection'; count: number };

const SHAPE_COLOR_PRESETS = [
  { name: 'Blue', fill: '3b82f6', border: '2563eb' },
  { name: 'Green', fill: '10b981', border: '059669' },
  { name: 'Amber', fill: 'f59e0b', border: 'd97706' },
  { name: 'Purple', fill: '8b5cf6', border: '7c3aed' },
  { name: 'Pink', fill: 'ec4899', border: 'db2777' },
  { name: 'Red', fill: 'ef4444', border: 'dc2626' },
  { name: 'Slate', fill: '64748b', border: '475569' },
  { name: 'Cyan', fill: '06b6d4', border: '0891b2' },
];

export type ZOrder = 'front' | 'forward' | 'backward' | 'back';

export interface CtxActions {
  addNode: () => void;
  reorder: (direction: ZOrder) => void;
  paste: () => void;
  selectAll: () => void;
  layout: (dir: 'TB' | 'LR') => void;
  fitView: () => void;
  toggleGrid: () => void;
  importJson: () => void;
  importMermaid: () => void;
  clear: () => void;
  duplicate: () => void;
  copyName: () => void;
  changeIcon: () => void;
  toggleLock: () => void;
  changeColor: (fill: string, border: string) => void;
  ungroup: () => void;
  group: () => void;
  align: (t: AlignType) => void;
  distribute: (a: DistributeAxis) => void;
  deleteSelection: () => void;
  editEdge: () => void;
  setEdgeType: (type: string) => void;
  setEdgeArrows: (mode: 'none' | 'end' | 'both') => void;
  toggleEdgeAnim: () => void;
  resetEdgeLabel: () => void;
  deleteEdge: () => void;
}

interface EditorContextMenuProps {
  target: CtxTarget;
  showGrid: boolean;
  canPaste: boolean;
  actions: CtxActions;
}

const mod = typeof navigator !== 'undefined' && navigator.platform.toLowerCase().includes('mac') ? '⌘' : 'Ctrl';

function ArrangeSub({ actions }: { actions: CtxActions }) {
  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger>
        <Layers />
        Arrange
      </ContextMenuSubTrigger>
      <ContextMenuSubContent>
        <ContextMenuItem onSelect={() => actions.reorder('front')}>
          <BringToFront />
          Bring to front
          <ContextMenuShortcut>{mod} ⇧ ]</ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => actions.reorder('forward')}>
          <ArrowUp />
          Bring forward
          <ContextMenuShortcut>{mod} ]</ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => actions.reorder('backward')}>
          <ArrowDown />
          Send backward
          <ContextMenuShortcut>{mod} [</ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => actions.reorder('back')}>
          <SendToBack />
          Send to back
          <ContextMenuShortcut>{mod} ⇧ [</ContextMenuShortcut>
        </ContextMenuItem>
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}

function AlignSub({ actions }: { actions: CtxActions }) {
  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger>
        <AlignCenterHorizontal />
        Align
      </ContextMenuSubTrigger>
      <ContextMenuSubContent>
        <ContextMenuItem onSelect={() => actions.align('left')}><AlignStartVertical />Align left</ContextMenuItem>
        <ContextMenuItem onSelect={() => actions.align('center-h')}><AlignCenterVertical />Align centers</ContextMenuItem>
        <ContextMenuItem onSelect={() => actions.align('right')}><AlignEndVertical />Align right</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={() => actions.align('top')}><AlignStartHorizontal />Align top</ContextMenuItem>
        <ContextMenuItem onSelect={() => actions.align('center-v')}><AlignCenterHorizontal />Align middles</ContextMenuItem>
        <ContextMenuItem onSelect={() => actions.align('bottom')}><AlignEndHorizontal />Align bottom</ContextMenuItem>
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}

export function EditorContextMenu({ target, showGrid, canPaste, actions }: EditorContextMenuProps) {
  return (
    <ContextMenuContent className="w-56">
      {target.type === 'pane' && (
        <>
          <ContextMenuItem onSelect={actions.addNode}>
            <Plus />
            Add node
            <ContextMenuShortcut>{mod} K</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onSelect={actions.paste} disabled={!canPaste}>
            <Clipboard />
            Paste here
            <ContextMenuShortcut>{mod} V</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onSelect={actions.selectAll}>
            <MousePointerSquareDashed />
            Select all
            <ContextMenuShortcut>{mod} A</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuSub>
            <ContextMenuSubTrigger><Workflow />Auto-layout</ContextMenuSubTrigger>
            <ContextMenuSubContent>
              <ContextMenuItem onSelect={() => actions.layout('TB')}><Workflow />Vertical</ContextMenuItem>
              <ContextMenuItem onSelect={() => actions.layout('LR')}><Workflow className="rotate-90" />Horizontal</ContextMenuItem>
            </ContextMenuSubContent>
          </ContextMenuSub>
          <ContextMenuItem onSelect={actions.fitView}><Maximize />Fit view</ContextMenuItem>
          <ContextMenuCheckboxItem checked={showGrid} onCheckedChange={() => actions.toggleGrid()} closeOnSelect={false}>
            <Grid3X3 />
            Show grid
          </ContextMenuCheckboxItem>
          <ContextMenuSeparator />
          <ContextMenuItem onSelect={actions.importJson}><FolderOpen />Import JSON…</ContextMenuItem>
          <ContextMenuItem onSelect={actions.importMermaid}><Workflow />Import Mermaid…</ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem tone="destructive" onSelect={actions.clear}><Trash2 />Clear canvas</ContextMenuItem>
        </>
      )}

      {target.type === 'node' && (
        <>
          <ContextMenuLabel>{target.node.data?.label || (target.node.type === 'groupNode' ? 'Group' : 'Node')}</ContextMenuLabel>
          <ContextMenuItem onSelect={actions.duplicate}>
            <Copy />
            Duplicate
            <ContextMenuShortcut>{mod} D</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onSelect={actions.copyName}><Clipboard />Copy name</ContextMenuItem>
          {target.node.type === 'techNode' && (
            <ContextMenuItem onSelect={actions.changeIcon}><ImageIcon />Change icon…</ContextMenuItem>
          )}
          {target.node.type === 'shapeNode' && (
            <ContextMenuSub>
              <ContextMenuSubTrigger><Palette />Change color</ContextMenuSubTrigger>
              <ContextMenuSubContent className="w-auto">
                <div className="grid grid-cols-4 gap-1 p-1">
                  {SHAPE_COLOR_PRESETS.map((c) => (
                    <button
                      key={c.name}
                      type="button"
                      onClick={() => actions.changeColor(c.fill, c.border)}
                      title={c.name}
                      aria-label={c.name}
                      className="ss-press h-6 w-6 rounded-md border-2 transition-transform hover:scale-110"
                      style={{ backgroundColor: `#${c.fill}`, borderColor: `#${c.border}` }}
                    />
                  ))}
                </div>
              </ContextMenuSubContent>
            </ContextMenuSub>
          )}
          {target.node.type === 'groupNode' && (
            <ContextMenuItem onSelect={actions.ungroup}><Ungroup />Ungroup</ContextMenuItem>
          )}
          {/* Groups are pinned behind their children, so they can't be restacked. */}
          {target.node.type !== 'groupNode' && <ArrangeSub actions={actions} />}
          <ContextMenuItem onSelect={actions.toggleLock}>
            {target.node.data?.locked ? <Unlock /> : <Lock />}
            {target.node.data?.locked ? 'Unlock' : 'Lock'}
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem tone="destructive" onSelect={actions.deleteSelection}>
            <Trash2 />
            Delete
            <ContextMenuShortcut>⌫</ContextMenuShortcut>
          </ContextMenuItem>
        </>
      )}

      {target.type === 'selection' && (
        <>
          <ContextMenuLabel>{target.count} selected</ContextMenuLabel>
          <ContextMenuItem onSelect={actions.group}><Boxes />Group selection</ContextMenuItem>
          <ArrangeSub actions={actions} />
          <AlignSub actions={actions} />
          {target.count >= 3 && (
            <ContextMenuSub>
              <ContextMenuSubTrigger><AlignHorizontalDistributeCenter />Distribute</ContextMenuSubTrigger>
              <ContextMenuSubContent>
                <ContextMenuItem onSelect={() => actions.distribute('horizontal')}><AlignHorizontalDistributeCenter />Horizontally</ContextMenuItem>
                <ContextMenuItem onSelect={() => actions.distribute('vertical')}><AlignVerticalDistributeCenter />Vertically</ContextMenuItem>
              </ContextMenuSubContent>
            </ContextMenuSub>
          )}
          <ContextMenuItem onSelect={actions.duplicate}>
            <Copy />
            Duplicate
            <ContextMenuShortcut>{mod} D</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem tone="destructive" onSelect={actions.deleteSelection}>
            <Trash2 />
            Delete {target.count}
          </ContextMenuItem>
        </>
      )}

      {target.type === 'edge' && (
        <>
          <ContextMenuLabel>Connection</ContextMenuLabel>
          <ContextMenuItem onSelect={actions.editEdge}><Pencil />Edit…</ContextMenuItem>
          <ContextMenuSub>
            <ContextMenuSubTrigger><Spline />Line type</ContextMenuSubTrigger>
            <ContextMenuSubContent>
              <ContextMenuItem onSelect={() => actions.setEdgeType('smoothstep')}>Smooth</ContextMenuItem>
              <ContextMenuItem onSelect={() => actions.setEdgeType('default')}>Curved</ContextMenuItem>
              <ContextMenuItem onSelect={() => actions.setEdgeType('step')}>Step</ContextMenuItem>
              <ContextMenuItem onSelect={() => actions.setEdgeType('straight')}>Straight</ContextMenuItem>
            </ContextMenuSubContent>
          </ContextMenuSub>
          <ContextMenuSub>
            <ContextMenuSubTrigger><Spline />Arrows</ContextMenuSubTrigger>
            <ContextMenuSubContent>
              <ContextMenuItem onSelect={() => actions.setEdgeArrows('none')}>None</ContextMenuItem>
              <ContextMenuItem onSelect={() => actions.setEdgeArrows('end')}>End →</ContextMenuItem>
              <ContextMenuItem onSelect={() => actions.setEdgeArrows('both')}>Both ↔</ContextMenuItem>
            </ContextMenuSubContent>
          </ContextMenuSub>
          <ContextMenuItem onSelect={actions.toggleEdgeAnim}>
            <Play />
            {target.edge.animated ? 'Stop flow' : 'Animate flow'}
          </ContextMenuItem>
          {/* Only meaningful once the label has actually been dragged off centre. */}
          {target.edge.data?.labelT !== undefined && (
            <ContextMenuItem onSelect={actions.resetEdgeLabel}>
              <Crosshair />
              Recentre label
            </ContextMenuItem>
          )}
          <ContextMenuSeparator />
          <ContextMenuItem tone="destructive" onSelect={actions.deleteEdge}>
            <Trash2 />
            Delete connection
          </ContextMenuItem>
        </>
      )}
    </ContextMenuContent>
  );
}

export default EditorContextMenu;
